import { AGWASuiteApiClientBase, ApiResponse } from '../agwasuite-api';
import {
  AssignRoleSetsInput,
  AssignRoleSetsResult,
  AttachDomainScopeInput,
  AttachDomainScopeResult,
  GetGroupDomainScopesResult,
  GetGroupRoleSetsResult,
  GetSecurityDomainsResult,
  LoginSecurityContext,
  RemoveRoleSetAssignmentResult,
} from '../agwa-api-model';

export class SecurityApi extends AGWASuiteApiClientBase {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  async getSecurityDomains(): Promise<ApiResponse<GetSecurityDomainsResult>> {
    return this.get<GetSecurityDomainsResult>('/api/securitydomains');
  }

  async attachDomainScope(
    domainCode: string,
    input: AttachDomainScopeInput
  ): Promise<ApiResponse<AttachDomainScopeResult>> {
    const normalizedInput: AttachDomainScopeInput = {
      ...input,
      domainCode,
    };
    const path = `/api/securitydomains/${encodeURIComponent(
      domainCode
    )}/attach`;
    return this.post<AttachDomainScopeInput, AttachDomainScopeResult>(
      path,
      normalizedInput
    );
  }

  async detachDomainScope(
    scopeId: string,
    groupId: string,
    forceDelete = false
  ): Promise<ApiResponse<unknown>> {
    const params = new URLSearchParams({
      groupId,
      forceDelete: String(forceDelete),
    });
    const path = `/api/securitydomains/scopes/${encodeURIComponent(
      scopeId
    )}?${params.toString()}`;
    return this.delete<unknown>(path);
  }

  /** Deactivate a domain scope (soft): the member behaves as though the domain were never attached. */
  async deactivateDomainScope(
    scopeId: string,
    groupId: string
  ): Promise<ApiResponse<unknown>> {
    const params = new URLSearchParams({ groupId });
    const path = `/api/securitydomains/scopes/${encodeURIComponent(
      scopeId
    )}/disable?${params.toString()}`;
    return this.post<Record<string, never>, unknown>(path, {});
  }

  /** Reactivate a previously deactivated domain scope: restores its roles, reach, and instance. */
  async reactivateDomainScope(
    scopeId: string,
    groupId: string
  ): Promise<ApiResponse<unknown>> {
    const params = new URLSearchParams({ groupId });
    const path = `/api/securitydomains/scopes/${encodeURIComponent(
      scopeId
    )}/reactivate?${params.toString()}`;
    return this.post<Record<string, never>, unknown>(path, {});
  }

  /** Hard-delete a domain scope. Server gates this on the scope being a bare header with no data. */
  async hardDeleteDomainScope(
    scopeId: string,
    groupId: string
  ): Promise<ApiResponse<unknown>> {
    const params = new URLSearchParams({ groupId });
    const path = `/api/securitydomains/scopes/${encodeURIComponent(
      scopeId
    )}?${params.toString()}`;
    return this.delete<unknown>(path);
  }

  async getGroupDomainScopes(
    groupId: string
  ): Promise<ApiResponse<GetGroupDomainScopesResult>> {
    const path = `/api/securitygroups/${encodeURIComponent(groupId)}/domains`;
    return this.get<GetGroupDomainScopesResult>(path);
  }

  async getGroupRoleSets(
    groupId: string
  ): Promise<ApiResponse<GetGroupRoleSetsResult>> {
    const path = `/api/securitygroups/${encodeURIComponent(groupId)}/role-sets`;
    return this.get<GetGroupRoleSetsResult>(path);
  }

  async assignRoleSets(
    groupId: string,
    input: AssignRoleSetsInput
  ): Promise<ApiResponse<AssignRoleSetsResult>> {
    const path = `/api/securitygroups/${encodeURIComponent(groupId)}/role-sets`;
    const normalizedInput: AssignRoleSetsInput = {
      ...input,
      loginGroupId: groupId,
    };
    return this.post<AssignRoleSetsInput, AssignRoleSetsResult>(
      path,
      normalizedInput
    );
  }

  async removeRoleSetAssignment(
    groupId: string,
    assignmentId: string,
    forceDelete = false
  ): Promise<ApiResponse<RemoveRoleSetAssignmentResult>> {
    const params = new URLSearchParams({
      forceDelete: String(forceDelete),
    });
    const path = `/api/securitygroups/${encodeURIComponent(
      groupId
    )}/role-sets/${encodeURIComponent(assignmentId)}?${params.toString()}`;
    return this.delete<RemoveRoleSetAssignmentResult>(path);
  }

  async getGroupRoleSummary(
    groupId: string
  ): Promise<ApiResponse<LoginSecurityContext>> {
    const path = `/api/securitygroups/${encodeURIComponent(groupId)}/role-summary`;
    return this.get<LoginSecurityContext>(path);
  }
}
